/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  stock: 'ALL',
  date_start: Func.dateTimeAdd(-30),
  date_stop: Func.dateTimeNow(),
};

const initSummary = {
  quantity: '',
};

function RpImportStore() {
  const mod = 'rp_import_store';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'pd.barcode', label: 'Barcode', align: 'left' },
    { id: 'pd.code', label: 'สินค้า', align: 'left' },
    { id: 'im.code', label: 'เลขรับเข้า', align: 'center' },
    { id: 'es.code', label: 'เลขที่กล่อง', align: 'center' },
    { id: 'im.branch_id', label: 'สาขา', align: 'center' },
    { id: 'tb.lot', label: 'LOT', align: 'center' },
    { id: 'tb.quantity', label: 'จำนวนรับ', align: 'right' },
    { id: 'pd.unit_name', label: 'หน่วย', align: 'left' },
    { id: 'stock_quantity', label: 'คลัง', align: 'right', sort: false },
    { id: 'stock_quantity_use', label: 'เบิก', align: 'right', sort: false },
    { id: 'stock_quantity_balance', label: 'คงเหลือ', align: 'right', sort: false },
    { id: 'tb.date_approve', label: 'วันที่', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setSummary(initSummary);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      stock: data.stock,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
    // window.open(`${urlExcel}/${param}`, "_blank");
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setUrlExcel(result.excel);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const expireDisplay = (item) => {
    if (item.is_expire === 'N') return '-';

    return Func.dateDisplay(item.date_expire);
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={5}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          {find.period === 'ALL' || find.branch === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={1}>
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='gradient' color='success' onClick={excel}>
                  <Icon>table_view</Icon>&nbsp;Excel
                </MDButton>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const cutStock = (row) => {
    if (row.addstock === 'Y') {
      return (
        <code className='color-success'>
          <i className='far fa-store-alt' /> เพิ่ม LOT เดิม
        </code>
      );
    }

    return (
      <code className='color-info'>
        <i className='far fa-store-alt' /> สร้าง LOT ใหม่
      </code>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_warehouse'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.barcode2}</MDBox>
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.import_code}
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.export_code}</MDBox>
                        <MDBox className='nowrap'>{Func.statusFaDisplay(row)}</MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.lot}</MDBox>
                        <MDBox className='nowrap'>{expireDisplay(row)}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap color-info'>
                        {Func.numberDisplay(row.quantity)}
                      </TableCell>
                      <TableCell className='nowrap'>{row.unit_name}</TableCell>
                      <TableCell align='right' className='nowrap color-danger'>
                        {row.stock === 'N' ? '-' : Func.numberDisplay(row.stock_quantity)}
                      </TableCell>
                      <TableCell align='right' className='nowrap color-warning'>
                        {row.stock === 'N' ? '-' : Func.numberDisplay(row.stock_quantity_use)}
                      </TableCell>
                      <TableCell align='right' className='nowrap color-success'>
                        {row.stock === 'N' ? '-' : Func.numberDisplay(row.stock_quantity_balance)}
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={7}>
                      รวม
                    </TableCell>
                    <TableCell align='right' className='color-info'>
                      {Func.numberDisplay(summary.quantity)}
                    </TableCell>
                    <TableCell colSpan={5} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_stock`), t(`menu.rp_import_store`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpImportStore;
